/* ============================================================================
 * Pawn Shop Tutorial — the guided walkthrough
 * ----------------------------------------------------------------------------
 * Reached from the sidebar's "Tutorial" entry (/pawn/tutorial). A chaptered tour
 * of every screen in the module: what it is for, its fields one by one, and a
 * worked example in rupees. The copy lives in pawnTutorial/tutorialContent.js
 * and its presentation in pawnTutorial/TutorialBlocks.js — this file is only the
 * chrome: the chapter navigator, the progress bar and prev/next.
 *
 * Two deliberate behaviours:
 *
 *  · Every chapter that describes a real screen carries an "Open this screen"
 *    button. Pressing it NAVIGATES rather than opening a second window (the app
 *    also runs inside Capacitor on phones, where a second window doesn't exist),
 *    so the tutorial has to be able to pick up where it left off —
 *
 *  · …which is what the localStorage progress does. The chapter you were on and
 *    the chapters you have read are remembered across visits and across
 *    sessions, so trying a screen and coming back costs nothing.
 *
 * Layout follows the report screens' rule: the PAGE never scrolls, the panes
 * scroll inside themselves, so the heading and the prev/next footer stay put.
 * Below `md` the chapter rail is replaced by a dropdown — a 15-item list and a
 * chapter body cannot share a phone's width.
 * ========================================================================== */

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  SchoolRounded as TutorialIcon,
  ArrowBackRounded as PrevIcon,
  ArrowForwardRounded as NextIcon,
  OpenInNewRounded as OpenScreenIcon,
  CheckCircleRounded as DoneIcon,
  RestartAltRounded as RestartIcon,
} from "@mui/icons-material";
import CHAPTERS from "./pawnTutorial/tutorialContent";
import TutorialBlock from "./pawnTutorial/TutorialBlocks";
import PawnNavButtons from "./pawnNavButtons";

// ─── Blue/White design tokens (shared with every other pawn screen) ──────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const INK_FAINT = "#94A3B8";
const BORDER = "#E2E8F0";
const BG_ALT = "#F1F5F9";
const GREEN = "#15803D";

/* Where the reader was and what they have read. One key, one small object — the
 * tutorial is not worth a context or a server round trip, but losing your place
 * because you went to look at the Payment Screen would make the "Open this
 * screen" button useless. */
const STORE_KEY = "pawn_tutorial_progress";

const loadProgress = () => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { lastId: null, read: [] };
    const parsed = JSON.parse(raw);
    return {
      lastId: typeof parsed?.lastId === "string" ? parsed.lastId : null,
      read: Array.isArray(parsed?.read)
        ? parsed.read.filter((x) => typeof x === "string")
        : [],
    };
  } catch {
    // A corrupt or unavailable store just means starting from chapter 1.
    return { lastId: null, read: [] };
  }
};

const saveProgress = (progress) => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(progress));
  } catch {
    /* private mode / quota — the tour still works, it just won't be remembered */
  }
};

const PawnTutorial = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  // noSsr: without it the first render always answers false, so a phone would
  // paint one frame of the desktop two-pane layout before correcting itself.
  const isWide = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });

  const initial = useRef(loadProgress()).current;

  const startIndex = useMemo(() => {
    const i = CHAPTERS.findIndex((c) => c.id === initial.lastId);
    return i >= 0 ? i : 0;
  }, [initial.lastId]);

  const [index, setIndex] = useState(startIndex);
  const [read, setRead] = useState(() => new Set(initial.read));

  const chapter = CHAPTERS[index];
  const bodyRef = useRef(null);

  /* Landing on a chapter marks it read and remembers it as the place to resume.
   * Marking on ARRIVAL rather than on leaving is the honest reading of "where
   * was I": someone who opens chapter 6, jumps to the Payment Screen and comes
   * back wants chapter 6, not chapter 5. */
  useEffect(() => {
    if (!chapter) return;
    setRead((prev) => {
      if (prev.has(chapter.id)) {
        saveProgress({ lastId: chapter.id, read: Array.from(prev) });
        return prev;
      }
      const next = new Set(prev);
      next.add(chapter.id);
      saveProgress({ lastId: chapter.id, read: Array.from(next) });
      return next;
    });
  }, [chapter]);

  // Moving between chapters must start at the top of the new one — the body is
  // its own scroll container, so the page-level scroll restore can't do it.
  const goTo = useCallback((nextIndex) => {
    const clamped = Math.max(0, Math.min(CHAPTERS.length - 1, nextIndex));
    setIndex(clamped);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, []);

  const handleRestart = () => {
    setRead(new Set());
    saveProgress({ lastId: CHAPTERS[0].id, read: [] });
    goTo(0);
  };

  const readCount = read.size;
  const pct = Math.round((readCount / CHAPTERS.length) * 100);

  // ── Chapter rail (md and up) ───────────────────────────────────────────────
  const chapterRail = (
    <Box
      sx={{
        width: 268,
        flexShrink: 0,
        bgcolor: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.63rem",
            fontWeight: 700,
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            color: INK_FAINT,
          }}
        >
          Chapters
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: INK_MUTED }}>
          {readCount} / {CHAPTERS.length}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 1,
          py: 1,
          // Hidden scrollbar, as on the sidebar and the report tables.
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {CHAPTERS.map((c, i) => {
          const active = i === index;
          const done = read.has(c.id) && !active;
          return (
            <Box
              key={c.id}
              onClick={() => goTo(i)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.9,
                mb: 0.4,
                borderRadius: "9px",
                cursor: "pointer",
                bgcolor: active ? BLUE_PALE : "transparent",
                borderRight: active
                  ? `3px solid ${BLUE}`
                  : "3px solid transparent",
                "&:hover": { bgcolor: active ? BLUE_PALE : "#EEF2F6" },
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  borderRadius: "7px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.66rem",
                  fontWeight: 800,
                  bgcolor: active ? BLUE : done ? "transparent" : BG_ALT,
                  color: active ? "#fff" : done ? GREEN : INK_MUTED,
                }}
              >
                {done ? <DoneIcon sx={{ fontSize: 17 }} /> : i + 1}
              </Box>
              <Typography
                noWrap
                sx={{
                  fontSize: "0.79rem",
                  fontWeight: active ? 700 : 500,
                  color: active ? BLUE_DARK : INK_SOFT,
                  minWidth: 0,
                }}
              >
                {c.navTitle || c.title}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ p: 1.25, borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <Button
          fullWidth
          size="small"
          startIcon={<RestartIcon sx={{ fontSize: 16 }} />}
          onClick={handleRestart}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: INK_MUTED,
            "&:hover": { bgcolor: BG_ALT },
          }}
        >
          Start over
        </Button>
      </Box>
    </Box>
  );

  /* ── Chapter header ─────────────────────────────────────────────────────────
   * Rendered in one of two places (see the JSX below): pinned above the scroll
   * area on a wide screen, and INSIDE the scroll area on a phone — chip + title
   * + tagline + picker comes to nearly half a phone's height, and pinning all of
   * it would leave the prose reading through a letterbox. */
  const chapterHeader = (
    <Box
      sx={{
        px: { xs: 0, md: 2.5 },
        pb: { xs: 2, md: 2 },
        pt: { xs: 0, md: 2 },
        mb: { xs: 1, md: 0 },
        borderBottom: {
          xs: `1px dashed ${BORDER}`,
          md: `1px solid ${BORDER}`,
        },
        flexShrink: 0,
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Chip
            size="small"
            icon={React.cloneElement(chapter.icon, { sx: { fontSize: 15 } })}
            label={`Chapter ${index + 1} of ${CHAPTERS.length}`}
            sx={{
              height: 22,
              mb: 0.75,
              fontSize: "0.68rem",
              fontWeight: 700,
              bgcolor: BLUE_PALE,
              color: BLUE_DARK,
              border: `1px solid ${BORDER}`,
              "& .MuiChip-icon": { color: BLUE_DARK, ml: 0.6 },
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: "1.08rem", sm: "1.35rem" },
              fontWeight: 700,
              color: INK,
              letterSpacing: "-0.4px",
              lineHeight: 1.3,
            }}
          >
            {chapter.title}
          </Typography>
          {chapter.tagline && (
            <Typography
              sx={{
                fontSize: { xs: "0.8rem", sm: "0.86rem" },
                color: INK_MUTED,
                mt: 0.4,
                lineHeight: 1.55,
              }}
            >
              {chapter.tagline}
            </Typography>
          )}
        </Box>

        {chapter.path && (
          <Tooltip
            title="We'll remember this chapter so you can come straight back"
            placement="left"
            arrow
          >
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenScreenIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate(chapter.path)}
              sx={{
                flexShrink: 0,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                borderRadius: "9px",
                color: BLUE_DARK,
                borderColor: BLUE,
                "&:hover": { bgcolor: BLUE_PALE, borderColor: BLUE_DARK },
              }}
            >
              {chapter.pathLabel || "Open this screen"}
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Phone / portrait-tablet chapter picker — the rail's stand-in */}
      {!isWide && (
        <TextField
          select
          fullWidth
          size="small"
          value={index}
          onChange={(e) => goTo(Number(e.target.value))}
          sx={{
            mt: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: "9px",
              fontSize: "0.82rem",
              fontWeight: 600,
            },
          }}
        >
          {CHAPTERS.map((c, i) => (
            <MenuItem key={c.id} value={i} sx={{ fontSize: "0.82rem" }}>
              {i + 1}. {c.navTitle || c.title}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        // Vertical stays scrollable so a very short window degrades to a scroll
        // rather than clipping the footer away; horizontal is pinned shut.
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: BG_ALT,
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
      }}
    >
      {/* ── Page heading ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: { xs: 1.25, md: 2 },
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <PawnNavButtons />
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "11px",
              background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARK} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(37,99,235,0.28)",
            }}
          >
            <TutorialIcon sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                fontWeight: 700,
                color: INK,
                letterSpacing: "-0.3px",
                lineHeight: 1.25,
              }}
            >
              Pawn Shop Tutorial
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
              Every screen, every field, with real price examples
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: { xs: "100%", sm: 190 },
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: BORDER,
                "& .MuiLinearProgress-bar": { bgcolor: BLUE, borderRadius: 3 },
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: INK_MUTED,
              whiteSpace: "nowrap",
            }}
          >
            {pct}% read
          </Typography>
        </Box>
      </Box>

      {/* ── Two panes: chapter rail + chapter body ───────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: 420, md: 0 },
          minWidth: 0,
          display: "flex",
          gap: 2,
        }}
      >
        {isWide && chapterRail}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            bgcolor: "#fff",
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {isWide && chapterHeader}

          {/* Chapter body — the only part that scrolls */}
          <Box
            ref={bodyRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              px: { xs: 1.75, sm: 2.5 },
              py: { xs: 1.75, sm: 2.25 },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {!isWide && chapterHeader}

            {chapter.blocks.map((block, i) => (
              <TutorialBlock key={i} block={block} />
            ))}

            {/* End-of-chapter nudge, so the reader isn't left hanging mid-scroll */}
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: `1px dashed ${BORDER}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: "0.78rem", color: INK_FAINT }}>
                {index < CHAPTERS.length - 1
                  ? `Up next — ${CHAPTERS[index + 1].title}`
                  : "That's the last chapter. Nicely done."}
              </Typography>
              {index < CHAPTERS.length - 1 && (
                <Button
                  size="small"
                  endIcon={<NextIcon sx={{ fontSize: 16 }} />}
                  onClick={() => goTo(index + 1)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    color: BLUE_DARK,
                    "&:hover": { bgcolor: BLUE_PALE },
                  }}
                >
                  Continue
                </Button>
              )}
            </Box>
          </Box>

          {/* Pinned prev/next footer */}
          <Box
            sx={{
              px: { xs: 1.25, sm: 2 },
              py: 1.25,
              borderTop: `1px solid ${BORDER}`,
              bgcolor: BG_ALT,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              startIcon={<PrevIcon sx={{ fontSize: 17 }} />}
              disabled={index === 0}
              onClick={() => goTo(index - 1)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.8rem",
                color: INK_SOFT,
                "&:hover": { bgcolor: "#E2E8F0" },
                "&.Mui-disabled": { color: INK_FAINT },
              }}
            >
              Back
            </Button>

            {/* Dot strip — a compact sense of where you are in the whole tour */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {CHAPTERS.map((c, i) => (
                <Tooltip key={c.id} title={c.title} arrow>
                  <IconButton size="small" onClick={() => goTo(i)} sx={{ p: 0.35 }}>
                    <Box
                      sx={{
                        width: i === index ? 18 : 7,
                        height: 7,
                        borderRadius: 4,
                        bgcolor:
                          i === index
                            ? BLUE
                            : read.has(c.id)
                              ? "#93C5FD"
                              : "#CBD5E1",
                        transition: "width 0.2s ease, background-color 0.2s ease",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ))}
            </Box>

            <Button
              size="small"
              variant="contained"
              endIcon={<NextIcon sx={{ fontSize: 17 }} />}
              disabled={index === CHAPTERS.length - 1}
              onClick={() => goTo(index + 1)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.8rem",
                borderRadius: "9px",
                boxShadow: "none",
                bgcolor: BLUE,
                "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PawnTutorial;
